import React, { useEffect, useState, useMemo, useRef } from "react"
import styles from "./agentBuilder.module.css"
import api from "../../services/api"
import { toast } from "react-toastify"
import { useParams } from "react-router-dom"
import { formatFullDate, formatRelativeDate, getPropertyForItemInArrayById, truncateText, wasRecentlyUpdated } from "../../utils/utils"
import { useData } from "../../contexts/dataContext"
import { useWebSocketChatContext } from "../../contexts/webSocketChatContext"
import { MdAutoAwesome, MdOutlineDelete, MdOutlineEmojiEmotions, MdOutlineSend } from "react-icons/md"
import BackButton from "../../components/BackButton"
import Button from "../../components/Form/Button"
import FormGroup from "../../components/Form/FormGroup"
import Input from "../../components/Form/Input"
import Select from "../../components/Form/Select"
import Emojis from "../../components/Chat/Emojis"
import RichTextarea, { insertTextAtCursor } from "../../components/Chat/RichTextarea"
import ModalAgent from "../../components/Modal/ModalAgent"
import ModalAgentWizard from "../../components/Modal/ModalAgent/ModalAgentWizard"
import ModalAgentReview from "../../components/Modal/ModalAgent/ModalAgentReview"
import ModalDelete from "../../components/Modal/ModalDelete"
import ModalKnowledgeBase from "../../components/Modal/ModalKnowledgeBase"
import Loading from "../layouts/Loading"
import { buildAgentReviewUpdate } from "./agentReviewPayload"
import { AI_TEST_MESSAGE_DEBOUNCE_MS, buildAITestMessageBatch } from "./aiTestMessageBatch"

const TABS = [
    { id: "agents", label: "Agentes" },
    { id: "knowledge", label: "Bases de Conhecimento" },
    { id: "tests", label: "Testes" }
]

const typeOptions = [
    { id: "text", value: "Texto" },
    { id: "url", value: "URL" },
    { id: "file", value: "Arquivo" },
]

const DRAFT_TEST_SESSION_ID = "draft-test-session"

const isDraftTestSession = (session) => session?.isDraft || session?._id === DRAFT_TEST_SESSION_ID

const hasActiveTestDraft = (session, messages = []) => isDraftTestSession(session) && messages.length === 0

const getAgentTestMetadata = (agent) => ({
    agent_name: agent?.name,
    agent_version: agent?.version,
    model_provider: agent?.model?.provider || "openai",
    model_name: agent?.model?.name || "gpt-4o-mini",
    model_temperature: agent?.model?.temperature ?? 0.3
})

const createDraftTestSession = (agent) => ({
    _id: DRAFT_TEST_SESSION_ID,
    isDraft: true,
    name: "Novo atendimento de teste",
    status: "draft",
    agent_id: agent?._id,
    updated_at: new Date().toISOString(),
    metadata: getAgentTestMetadata(agent)
})

const getSessionAgentLabel = (session) => {
    const name = session?.metadata?.agent_name
    const version = session?.metadata?.agent_version
    return [name, version ? `v${version}` : ""].filter(Boolean).join(" ")
}

const getSessionModelLabel = (session) => {
    const provider = session?.metadata?.model_provider
    const model = session?.metadata?.model_name
    const temperature = session?.metadata?.model_temperature
    const modelLabel = [provider, model].filter(Boolean).join(" / ")

    if (!modelLabel) return ""

    return typeof temperature === "number" ? `${modelLabel} • temp. ${temperature}` : modelLabel
}

const getActionReasonLabel = (reason) => {
    const labels = {
        "Fluxo IA": "resposta automática",
        "Baixa confiança": "baixa confiança na resposta",
        "Regra de handoff do agente": "regra de transferência do agente",
        "Ação de transferência da intenção": "ação configurada na intenção",
        "Usuário solicitou atendimento humano": "cliente pediu atendimento humano",
        "Resposta indicou transferência para humano": "resposta solicitou transferência",
        "Transferência confirmada na conversa": "transferência confirmada na conversa",
        "Mensagem vazia": "mensagem vazia",
        "Coleta de campos obrigatórios da intenção": "coleta de informações",
        "Ferramenta validada sem execução real": "nenhuma alteração foi realizada",
    }

    return labels[reason] || reason
}

const formatActionReason = (reason) => reason ? ` • ${getActionReasonLabel(reason)}` : ""

const getTestActionLabel = (action) => {
    switch (action?.type) {
        case "transfer":
            return `Ação: transferir atendimento${formatActionReason(action.reason)}`
        case "collect_data":
            return `Ação: coletar dados${formatActionReason(action.reason)}`
        case "ignore":
            return `Ação: ignorar mensagem${formatActionReason(action.reason)}`
        case "reply":
            return `Ação: responder automaticamente${formatActionReason(action.reason)}`
        case "fixed_response":
            return `Ação: resposta fixa${formatActionReason(action.reason)}`
        case "call_tool":
            return `Ação: usar ferramenta em modo de teste${formatActionReason(action.reason)}`
        default:
            return ""
    }
}

const getOptionLabelById = (options = [], id) => {
    if (!id) return ""

    const item = options.find(option => option?.id?.toString() === id?.toString() || option?._id?.toString() === id?.toString())
    return item?.value || item?.name || item?.email || ""
}

const getTestTransferTargetLabel = (action, { teams = [], roles = [], users = [] } = {}) => {
    if (action?.type !== "transfer") return ""

    const target = action?.target
    if (!target?.assignTo) {
        return "Destino: atribuição padrão da caixa de entrada"
    }

    if (target.assignTo === "teamAndRole") {
        const teamName = getOptionLabelById(teams, target.team_id)
        const roleName = getOptionLabelById(roles, target.role_id)
        const parts = [
            teamName ? `Equipe ${teamName}` : "",
            roleName ? `Cargo ${roleName}` : ""
        ].filter(Boolean)

        return parts.length ? `Destino: ${parts.join(" • ")}` : "Destino: equipe/cargo configurado"
    }

    if (target.assignTo === "users") {
        const userNames = (target.users || [])
            .map(user => getOptionLabelById(users, user?.user_id || user))
            .filter(Boolean)

        return userNames.length ? `Destino: ${userNames.join(", ")}` : "Destino: usuários configurados"
    }

    return ""
}

const AgentBuilder = () => {
    const { account_id } = useParams()

    const { fields, teams, roles, users } = useData()
    const { lastJsonMessage, sendJsonMessage } = useWebSocketChatContext()

    const [agents, setAgents] = useState([])
    const [agent, setAgent] = useState({})
    const [showModalAgent, setShowModalAgent] = useState(false)
    const [showModalWizard, setShowModalWizard] = useState(false)
    const [reviewAgent, setReviewAgent] = useState(null)
    const [showModalDeleteAgent, setShowModalDeleteAgent] = useState(false)
    const [showModalDeleteKb, setShowModalDeleteKb] = useState(false)
    const [agentToDelete, setAgentToDelete] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [step, setStep] = useState(0)
    const [activeTab, setActiveTab] = useState("agents")

    const [knowledgeBases, setKnowledgeBases] = useState([])
    const [showModalKB, setShowModalKB] = useState(false)
    const [kb, setKb] = useState({})
    const [kbToDelete, setKbToDelete] = useState(null)

    const [searchAgents, setSearchAgents] = useState("")
    const [searchKB, setSearchKB] = useState("")
    const [sortBy, setSortBy] = useState("recent")
    const [testAgentId, setTestAgentId] = useState("")
    const [testAgentFilterId, setTestAgentFilterId] = useState("all")
    const [testSessions, setTestSessions] = useState([])
    const [testSession, setTestSession] = useState(null)
    const [testMessages, setTestMessages] = useState([])
    const [testInput, setTestInput] = useState("")
    const [isTesting, setIsTesting] = useState(false)
    const [isTestDebouncing, setIsTestDebouncing] = useState(false)
    const [showTestEmojis, setShowTestEmojis] = useState(false)
    const [editingTestName, setEditingTestName] = useState(false)
    const [testNameDraft, setTestNameDraft] = useState("")
    const testTextareaRef = useRef(null)
    const pendingAITestRef = useRef(null)
    const pendingAITestBatchRef = useRef([])
    const testBatchTimerRef = useRef(null)
    const pendingAIEvaluationRef = useRef(null)
    const hasDraftWithoutMessages = hasActiveTestDraft(testSession, testMessages)
    const hasSelectedTestSession = !!testSession?._id
    const canUseTestComposer = hasSelectedTestSession && testSession?.status !== "closed"

    useEffect(() => () => {
        if (testBatchTimerRef.current) clearTimeout(testBatchTimerRef.current)
    }, [])

    useEffect(() => {
        if (step === 0) {
            setTimeout(() => setStep(1), 500)
        }

        if (step === 1) {
            setTimeout(() => setStep(2), 2000)
        }

        if (step === 2) {
            setTimeout(() => setStep(3), 2000)
        }
    }, [step])

    const useTypewriter = (text, speed = 20, start = false) => {
        const [displayed, setDisplayed] = useState("")

        useEffect(() => {
            if (!start) return

            let i = 0
            const interval = setInterval(() => {
                setDisplayed(text.slice(0, i + 1))
                i++

                if (i >= text.length) clearInterval(interval)
            }, speed)

            return () => clearInterval(interval)
        }, [text, speed, start])

        return displayed
    }

    const msg1 = useTypewriter(
        "Oi! 👋 Como posso te ajudar?",
        20,
        step >= 1
    )

    const msg2 = useTypewriter(
        "Gostaria de saber o valor dos cursos",
        20,
        step >= 2
    )

    const msg3 = useTypewriter(
        "Claro! Temos cursos a partir de R$99/mês.",
        20,
        step >= 3
    )

    useEffect(() => {
        getAgents()
        getKnowledgeBases()
    }, [])

    useEffect(() => {
        if (activeTab === "tests" && !testAgentId && agents.length) {
            setTestAgentId(agents[0]._id)
        }
    }, [activeTab, agents, testAgentId])

    useEffect(() => {
        if (activeTab === "tests") {
            getTestSessions()
        }
    }, [activeTab])

    useEffect(() => {
        if (lastJsonMessage?.event !== "AI_TEST_MESSAGE_RESULT") return

        const pending = pendingAITestRef.current
        const content = lastJsonMessage.content || {}

        if (!pending || pending.request_id !== content.request_id) return

        clearTimeout(pending.timeout)
        pendingAITestRef.current = null

        if (content.error) {
            pending.reject(new Error(content.message || "Algo deu errado ao testar o agente."))
            return
        }

        pending.resolve(content.data)
    }, [lastJsonMessage])

    useEffect(() => {
        if (lastJsonMessage?.event !== "AI_AGENT_EVALUATION_RESULT") return
        const pending = pendingAIEvaluationRef.current
        const content = lastJsonMessage.content || {}
        if (!pending || pending.request_id !== content.request_id) return
        clearTimeout(pending.timeout)
        pendingAIEvaluationRef.current = null
        if (content.error) return pending.reject(new Error(content.message || "Não foi possível verificar o agente."))
        pending.resolve(content.data)
    }, [lastJsonMessage])

    const getAgents = () => {
        setIsLoading(true)
        api.get(`/ai/agents`)
            .then(res => {
                setAgents(res.data.data || [])
            })
            .catch(err => {
                toast.error(err?.response?.data?.message)
            })
            .finally(() => setIsLoading(false))
    }

    const getAgentById = async (agent_id) => {
        setIsLoading(true)
        try {
            const result = await api.get(`/ai/agents/${agent_id}`)
            return result?.data?.data
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const getTestSessions = async (options = {}) => {
        try {
            const res = await api.get(`/ai/test-sessions`)
            const sessions = res.data.data || []
            const currentDraft = options.preserveDraft === false ? null : isDraftTestSession(testSession) ? testSession : null
            const nextSessions = currentDraft ? [currentDraft, ...sessions] : sessions
            setTestSessions(nextSessions)

            if (currentDraft) return

            if (!testSession && sessions[0]) {
                const sessionAgentId = sessions[0].agent_id?._id || sessions[0].agent_id
                handleOpenTestSession(sessionAgentId, sessions[0]._id)
            }

            if (!sessions.length) {
                setTestSession(null)
                setTestMessages([])
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || "Não foi possível criar a base.")
        }
    }

    const handleCreateTestSession = async () => {
        const agentForDraft = testAgentFilterId !== "all" ? testAgentFilterId : testAgentId

        if (!agentForDraft) {
            return toast.error("Selecione um agente para testar")
        }

        const currentDraft = testSessions.find(isDraftTestSession)
        if (currentDraft) {
            setTestSession(currentDraft)
            setTestMessages([])
            return
        }

        setTestAgentId(agentForDraft)

        const draft = createDraftTestSession(agents.find(agent => agent._id === agentForDraft))
        setTestSessions(prev => [draft, ...prev])
        setTestSession(draft)
        setTestMessages([])
        setEditingTestName(false)
    }

    const handleChangeTestAgent = (agent_id) => {
        const selectedAgent = agents.find(agent => agent._id === agent_id)

        setTestAgentId(agent_id)
        setEditingTestName(false)

        if (hasDraftWithoutMessages) {
            const updatedDraft = {
                ...testSession,
                agent_id,
                metadata: getAgentTestMetadata(selectedAgent),
                updated_at: new Date().toISOString()
            }

            setTestSession(updatedDraft)
            setTestSessions(prev => [
                updatedDraft,
                ...prev.filter(session => !isDraftTestSession(session))
            ])
            return
        }

        setTestSessions([])
        setTestSession(null)
        setTestMessages([])
    }

    const handleOpenTestSession = async (agent_id, session_id) => {
        if (agent_id) {
            setTestAgentId(agent_id)
        }

        const draft = testSessions.find(session => session._id === session_id && isDraftTestSession(session))
        if (draft) {
            setTestSession(draft)
            setTestMessages([])
            setEditingTestName(false)
            return
        }

        setIsTesting(true)
        try {
            const res = await api.get(`/ai/agents/${agent_id}/test-sessions/${session_id}/messages`)
            setTestSession(res.data.data.session)
            setTestMessages(res.data.data.messages || [])
            setEditingTestName(false)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsTesting(false)
        }
    }

    const addTestEmoji = (emoji) => {
        insertTextAtCursor(testTextareaRef.current, testInput, emoji, setTestInput)
    }

    const handleTestInputKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            handleSendTestMessage()
        }
    }

    const sendAITestMessageBySocket = ({ agent_id, session_id, text, messages }) => {
        const request_id = `ai-test-${Date.now()}-${Math.random().toString(16).slice(2)}`

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                pendingAITestRef.current = null
                reject(new Error("Tempo limite ao testar o agente. Verifique a conexão do chat."))
            }, 60000)

            pendingAITestRef.current = {
                request_id,
                resolve,
                reject,
                timeout
            }

            sendJsonMessage({
                event: "AI_TEST_MESSAGE",
                message: {
                    request_id,
                    agent_id,
                    session_id,
                    text,
                    messages
                }
            })
        })
    }

    const flushAITestMessageBatch = async ({ agent_id, session_id }) => {
        const queuedMessages = pendingAITestBatchRef.current
        pendingAITestBatchRef.current = []
        testBatchTimerRef.current = null
        setIsTestDebouncing(false)
        if (!queuedMessages.length) return

        const payload = buildAITestMessageBatch(queuedMessages)
        const optimisticIds = new Set(queuedMessages.map(message => message.client_id))
        setIsTesting(true)

        try {
            const { user_message, user_messages, assistant_message } = await sendAITestMessageBySocket({
                agent_id,
                session_id,
                ...payload
            })
            const persistedUserMessages = user_messages?.length ? user_messages : [user_message].filter(Boolean)

            setTestMessages(prev => [
                ...prev.filter(message => !optimisticIds.has(message._id)),
                ...persistedUserMessages,
                assistant_message
            ].filter(Boolean))
            getTestSessions({ preserveDraft: false })
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || "Não foi possível testar o agente.")
        } finally {
            setIsTesting(false)
        }
    }

    const scheduleAITestMessage = ({ agent_id, session_id, message }) => {
        pendingAITestBatchRef.current = [...pendingAITestBatchRef.current, message]
        if (testBatchTimerRef.current) clearTimeout(testBatchTimerRef.current)
        setIsTestDebouncing(true)
        testBatchTimerRef.current = setTimeout(
            () => flushAITestMessageBatch({ agent_id, session_id }),
            AI_TEST_MESSAGE_DEBOUNCE_MS
        )
    }

    const runAutomaticAgentEvaluation = agent_id => {
        const request_id = `ai-evaluation-${Date.now()}-${Math.random().toString(16).slice(2)}`
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                pendingAIEvaluationRef.current = null
                reject(new Error("A verificação demorou mais que o esperado. Consulte a aba Testes antes de tentar novamente."))
            }, 180000)
            pendingAIEvaluationRef.current = { request_id, resolve, reject, timeout }
            sendJsonMessage({ event: "AI_RUN_AGENT_EVALUATION", message: { request_id, agent_id } })
        })
    }

    const handleStartRenameTestSession = () => {
        if (!testSession) return
        setTestNameDraft(testSession.name || "")
        setEditingTestName(true)
    }

    const handleCancelRenameTestSession = () => {
        setEditingTestName(false)
        setTestNameDraft("")
    }

    const handleSaveTestSessionName = async () => {
        const formattedName = testNameDraft.trim()

        if (!formattedName) {
            return toast.error("Informe um nome para o teste")
        }

        if (formattedName.length > 80) {
            return toast.error("O nome do teste pode ter até 80 caracteres")
        }

        if (isDraftTestSession(testSession)) {
            const updatedSession = { ...testSession, name: formattedName, updated_at: new Date().toISOString() }
            setTestSession(updatedSession)
            setTestSessions(prev => prev.map(session => isDraftTestSession(session) ? updatedSession : session))
            handleCancelRenameTestSession()
            return
        }

        setIsTesting(true)
        try {
            const res = await api.patch(`/ai/agents/${testAgentId}/test-sessions/${testSession._id}`, {
                name: formattedName
            })
            const updatedSession = res.data.data
            setTestSession(updatedSession)
            setTestSessions(prev => prev.map(session => session._id === updatedSession._id ? updatedSession : session))
            handleCancelRenameTestSession()
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsTesting(false)
        }
    }

    const handleSendTestMessage = async () => {
        const text = testInput.trim()
        if (!text) return
        if (!testSession?._id) {
            return toast.error("Crie ou selecione um teste antes de enviar uma mensagem")
        }

        let currentSession = testSession
        const sessionAgentId = currentSession.agent_id?._id || currentSession.agent_id || testAgentId
        if (!sessionAgentId) {
            return toast.error("Selecione o agente deste teste antes de enviar uma mensagem")
        }

        setTestInput("")
        setShowTestEmojis(false)
        try {
            if (!currentSession?._id || isDraftTestSession(currentSession)) {
                setIsTesting(true)
                const res = await api.post(`/ai/agents/${sessionAgentId}/test-sessions`, {
                    name: currentSession?.name || `Atendimento de teste ${new Date().toLocaleString("pt-BR")}`
                })
                currentSession = res.data.data
                setTestAgentId(sessionAgentId)
                setTestSession(currentSession)
                setTestSessions(prev => [
                    currentSession,
                    ...prev.filter(session => !isDraftTestSession(session) && session._id !== currentSession._id)
                ])
                setIsTesting(false)
            }

            const createdAt = new Date().toISOString()
            const optimistic = {
                _id: `local-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                role: "user",
                content: text,
                created_at: createdAt
            }
            setTestMessages(prev => [...prev, optimistic])
            scheduleAITestMessage({
                agent_id: sessionAgentId,
                session_id: currentSession._id,
                message: { text, client_id: optimistic._id, created_at: createdAt }
            })
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message)
            setIsTesting(false)
        }
    }

    const handleCloseTestSession = async () => {
        if (!testAgentId || !testSession?._id) return

        if (isDraftTestSession(testSession)) {
            setTestSessions(prev => prev.filter(session => !isDraftTestSession(session)))
            setTestSession(null)
            setTestMessages([])
            return
        }

        setIsTesting(true)
        try {
            await api.patch(`/ai/agents/${testAgentId}/test-sessions/${testSession._id}/close`)
            setTestSession(prev => ({ ...prev, status: "closed" }))
            setTestSessions(prev => prev.map(s => s._id === testSession._id ? { ...s, status: "closed" } : s))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsTesting(false)
        }
    }

    // 🔥 CREATE
    const handleCreateAgent = (data) => {
        setIsLoading(true)

        api.post(`/ai/agents`, data)
            .then(res => {
                setAgents(prev => [...prev, res.data.data])
                setShowModalAgent(false)
                setAgent({})
                toast.success("Agente salvo como inativo. Abra-o para testar e ativar.")
            })
            .catch(err => {
                toast.error(err?.response?.data?.message)
            })
            .finally(() => setIsLoading(false))
    }

    // 🔥 UPDATE
    const handleUpdateAgent = (data) => {
        setIsLoading(true)
        const agent_id = agent?._id
        api.patch(`/ai/agents/${agent_id}`, data)
            .then(res => {
                setAgents(prev =>
                    prev.map(a => a._id === agent_id ? res.data.data : a)
                )
                setShowModalAgent(false)
                setAgent({})
            })
            .catch(err => {
                toast.error(err?.response?.data?.message)
            })
            .finally(() => setIsLoading(false))
    }

    // 🔥 DELETE
    const handleDeleteAgent = () => {
        setIsLoading(true)

        api.delete(`/ai/agents/${agentToDelete._id}`)
            .then(() => {
                setAgents(prev =>
                    prev.filter(a => a._id !== agentToDelete._id)
                )
                setShowModalDeleteAgent(false)
                setAgentToDelete(null)
            })
            .catch(err => {
                toast.error(err?.response?.data?.message)
            })
            .finally(() => setIsLoading(false))
    }

    // 🔥 OPEN MODAL
    const handleOpenModalAgent = async (agent_id) => {
        if (!agent_id) {
            setAgent({})
            setShowModalAgent(true)
            return
        }

        const agentData = await getAgentById(agent_id)
        if (agentData) {
            setAgent(agentData)
            setShowModalAgent(true)
        }
    }

    const handleOpenAgentReview = async (agent_id) => {
        const agentData = await getAgentById(agent_id)
        if (agentData) setReviewAgent(agentData)
    }

    const handleApplyAgentReview = async (review, selectedIds) => {
        const payload = buildAgentReviewUpdate(review, selectedIds)
        try {
            setIsLoading(true)
            const response = await api.patch(`/ai/agents/${reviewAgent._id}`, payload)
            setAgents(previous => previous.map(item => item._id === reviewAgent._id ? response.data.data : item))
            setReviewAgent(null)
            toast.success(`Nova versão ${response.data.data.version} criada com sucesso.`)
        } catch (error) {
            toast.error(error?.response?.data?.message || "Não foi possível aplicar as sugestões.")
        } finally { setIsLoading(false) }
    }


    // KNOWLEDGE BASES
    const getKnowledgeBases = () => {
        api.get("/ai/knowledge-bases")
            .then(res => setKnowledgeBases(res.data.data || []))
            .catch(err => toast.error(err?.response?.data?.message))
    }

    const handleSubmitKB = (data) => {
        if (kb?._id) {
            return handleUpdateKB({ ...data, _id: kb._id })
        }
        return handleCreateKB(data)
    }

    const handleCreateKB = async (data) => {
        setIsLoading(true)
        try {
            const res = await api.post("/ai/knowledge-bases", {
                name: data.name,
                description: data.description,
                type: data.type,
                content: data.content,
                metadata: data.metadata,
                is_active: data.is_active
            })

            const kbCreated = res.data.data

            if (data.type === "file") {
                if (!data.file?.[0]) throw new Error("Selecione um arquivo para criar a base.")
                const formData = new FormData()
                formData.append("file", data.file[0])
                formData.append("knowledge_base_id", kbCreated._id)

                await api.post(`/ai/knowledge-bases/upload`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                })
            }

            setKnowledgeBases(prev => [...prev, kbCreated])
            setShowModalKB(false)

        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleUpdateKB = async (data) => {
        setIsLoading(true)
        try {
            const res = await api.patch(`/ai/knowledge-bases/${data._id}`, {
                name: data.name,
                description: data.description,
                type: data.type,
                content: data.content,
                metadata: data.metadata,
                is_active: data.is_active
            })

            if (data.type === "file" && data.file?.[0]) {
                const formData = new FormData()
                formData.append("file", data.file[0])
                formData.append("knowledge_base_id", data._id)

                await api.post(`/ai/knowledge-bases/upload`, formData)
            }

            setKnowledgeBases(prev =>
                prev.map(k => k._id === data._id ? res.data.data : k)
            )

            setShowModalKB(false)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteKB = async () => {
        setIsLoading(true)
        try {
            await api.delete(`/ai/knowledge-bases/${kbToDelete._id}`)
            setKnowledgeBases(prev =>
                prev.filter(k => k._id !== kbToDelete._id)
            )
            setShowModalDeleteKb(false)
            setKbToDelete(null)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setIsLoading(false)
        }
    }

    const filterAndSort = (list = []) => {
        let filtered = list.filter(item => {
            const term = (activeTab === "agents" ? searchAgents : searchKB).toLowerCase()

            return (
                item.name?.toLowerCase().includes(term) ||
                item.description?.toLowerCase().includes(term)
            )
        })

        const sorted = [...filtered]

        switch (sortBy) {
            case "name":
                sorted.sort((a, b) => a.name.localeCompare(b.name))
                break

            case "oldest":
                sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                break

            default:
                sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                break
        }

        return sorted
    }

    const renderFilters = (searchValue, setSearchValue) => (
        <FormGroup>
            <div className={styles.filters}>
                <Input
                    type="search"
                    placeholder="Buscar..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                />

                <Select
                    name="Ordenação"
                    placeholder="Selecione uma opção!"
                    selected={sortBy}
                    onChange={(value) => setSortBy(value)}
                    options={[
                        { id: "recent", value: "Mais recentes" },
                        { id: "oldest", value: "Mais antigos" },
                        { id: "name", value: "Nome (A-Z)" },
                    ]}
                />
            </div>
        </FormGroup>
    )

    const useDebounce = (value, delay = 300) => {
        const [debounced, setDebounced] = useState(value)

        useEffect(() => {
            const timeout = setTimeout(() => {
                setDebounced(value)
            }, delay)

            return () => clearTimeout(timeout)
        }, [value, delay])

        return debounced
    }

    const debouncedSearchAgents = useDebounce(searchAgents, 300)
    const debouncedSearchKB = useDebounce(searchKB, 300)

    const filteredAgents = useMemo(() => {
        return filterAndSort(agents, debouncedSearchAgents)
    }, [agents, debouncedSearchAgents, sortBy])

    const filteredKB = useMemo(() => {
        return filterAndSort(knowledgeBases, debouncedSearchKB)
    }, [knowledgeBases, debouncedSearchKB, sortBy])

    const filteredTestSessions = useMemo(() => {
        if (testAgentFilterId === "all") {
            return testSessions
        }

        return testSessions.filter(session => {
            if (isDraftTestSession(session)) return true

            const sessionAgentId = session.agent_id?._id || session.agent_id
            return sessionAgentId?.toString() === testAgentFilterId?.toString()
        })
    }, [testSessions, testAgentFilterId])

    useEffect(() => {
        if (activeTab !== "tests") return
        if (!testSessions.length || hasDraftWithoutMessages) return

        const currentSessionIsVisible = filteredTestSessions.some(session => session._id === testSession?._id)
        if (currentSessionIsVisible) return

        const nextSession = filteredTestSessions[0]
        if (nextSession) {
            handleOpenTestSession(nextSession.agent_id?._id || nextSession.agent_id, nextSession._id)
            return
        }

        setTestSession(null)
        setTestMessages([])
        setTestInput("")
    }, [activeTab, testAgentFilterId, testSessions, filteredTestSessions, hasDraftWithoutMessages])

    return (
        <div className={styles.container}>
            <BackButton fallback={`/${account_id}/integrations/chat`} />

            <div className={styles.tabs}>
                {TABS.map(tab => (
                    <Button
                        key={tab.id} variant={activeTab === tab.id ? "secondary" : "neutral"}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {activeTab === "agents" && (
                <div className={styles.content}>
                    <div className={styles.card}>

                        <div className={styles.header}>
                            <span className={styles.title}>🤖 Agentes</span>

                            <div className={styles.headerActions}>
                                <Button variant="secondary"
                                    onClick={() => setShowModalWizard(true)}
                                >
                                    Criar com IA ✨
                                </Button>

                                <Button variant="primary"
                                    onClick={() => handleOpenModalAgent()}
                                >
                                    Novo Agente
                                </Button>
                            </div>
                        </div>
                        {renderFilters(searchAgents, setSearchAgents)}

                        <div className={styles.body}>
                            {agents.length === 0 && !isLoading && (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIcon}>🤖</div>

                                    <h3>Nenhum agente criado ainda</h3>

                                    <p>
                                        Crie um agente de IA para automatizar atendimentos,
                                        responder clientes e executar ações automaticamente.
                                    </p>

                                    <div className={styles.chatPreview}>
                                        {step >= 1 && (
                                            <div className={`${styles.msg} ${styles.bot}`}>
                                                {msg1}
                                            </div>
                                        )}

                                        {step >= 2 && (
                                            <div className={`${styles.msg} ${styles.user}`}>
                                                {msg2}
                                            </div>
                                        )}

                                        {step >= 3 && (
                                            <div className={`${styles.msg} ${styles.bot}`}>
                                                {msg3}
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.emptyActions}>
                                        <Button variant="secondary"
                                            onClick={() => setShowModalWizard(true)}
                                        >
                                            Criar com IA ✨
                                        </Button>

                                        <Button variant="primary"
                                            onClick={() => handleOpenModalAgent()}
                                        >
                                            Criar manualmente
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {agents.length > 0 && filteredAgents.length === 0 && !isLoading && (
                                <div className={styles.emptyState}>
                                    <h3>Nenhum resultado encontrado</h3>
                                    <p>Tente ajustar sua busca.</p>
                                </div>
                            )}
                            {filteredAgents.map(a => {
                                const wasUpdated = wasRecentlyUpdated(a.created_at, a.updated_at)

                                return (
                                    <div key={a._id} className={styles.agentCard}>
                                        <div>
                                            <strong>
                                                {a.name}
                                                {a.version && (
                                                    <span className={styles.metaInline}>
                                                        v{a.version}
                                                    </span>
                                                )}
                                            </strong>

                                            <span>{truncateText(a.description, 75)}</span>

                                            <span
                                                className={`${styles.badge} ${a.is_active
                                                    ? styles.success
                                                    : styles.inactive
                                                    }`}
                                            >
                                                {a.is_active ? "Ativo" : "Inativo"}
                                            </span>

                                            <div className={styles.meta}>
                                                <small
                                                    title={`Criado em ${formatFullDate(a.created_at)}${wasUpdated ? ` • Atualizado em ${formatFullDate(a.updated_at)}` : ''
                                                        }`}
                                                >
                                                    Criado {formatRelativeDate(a.created_at)}
                                                    {wasUpdated && ` • Atualizado ${formatRelativeDate(a.updated_at)}`}
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.actions}>
                                            <Button className={styles.assistantButton} variant="secondary" onClick={() => handleOpenAgentReview(a._id)} aria-label={`Abrir assistente de melhoria de ${a.name}`} title="Assistente de melhoria: analise e aprimore este agente com IA">
                                                <MdAutoAwesome />
                                            </Button>
                                            <Button variant="neutral"
                                                onClick={() => handleOpenModalAgent(a._id)}
                                            >
                                                Editar
                                            </Button>

                                            <Button
                                                className={styles.danger}
                                                onClick={() => {
                                                    setAgentToDelete(a)
                                                    setShowModalDeleteAgent(true)
                                                }}
                                            >
                                                Excluir
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 MODAL AGENT */}
            {showModalAgent && (
                <ModalAgent
                    agent={agent}
                    fields={fields}
                    onRunAutomaticEvaluation={runAutomaticAgentEvaluation}
                    onSubmit={
                        agent?._id
                            ? handleUpdateAgent
                            : handleCreateAgent
                    }
                    closeModal={() => {
                        setShowModalAgent(false)
                        setAgent({})
                    }}
                />
            )}

            {/* 🔥 WIZARD IA */}
            {showModalWizard && (
                <ModalAgentWizard
                    fields={fields}
                    closeModal={() => setShowModalWizard(false)}
                    onComplete={(data) => {
                        setShowModalWizard(false)
                        setAgent(data)
                        setShowModalAgent(true)
                    }}
                />
            )}

            {/* 🔥 DELETE AGENT */}
            {showModalDeleteAgent && (
                <ModalDelete
                    closeModal={() => {
                        setShowModalDeleteAgent(false)
                        setAgentToDelete(null)
                    }}
                    onSubmit={handleDeleteAgent}
                    icon={<MdOutlineDelete />}
                    title="Exclusão de agente"
                    description="Deseja realmente excluir o agente?"
                    id={agentToDelete?._id}
                />
            )}

            {/* 🔥 DELETE KB */}
            {showModalDeleteKb && (
                <ModalDelete
                    closeModal={() => {
                        setShowModalDeleteKb(false)
                        setKbToDelete(null)
                    }}
                    onSubmit={handleDeleteKB}
                    icon={<MdOutlineDelete />}
                    title="Exclusão de base de conhecimento"
                    description="Deseja realmente excluir a base de conhecimento?"
                    id={kbToDelete?._id}
                />
            )}

            {activeTab === "knowledge" && (
                <div className={styles.content}>
                    <div className={styles.card}>
                        <div className={styles.header}>
                            <span className={styles.title}>📚 Bases de conhecimento</span>

                            <div style={{ display: "flex", gap: 10 }}>
                                <Button variant="primary"
                                    onClick={() => {
                                        setKb({})
                                        setShowModalKB(true)
                                    }}
                                >
                                    Nova Base de Conhecimento
                                </Button>
                            </div>
                        </div>
                        {renderFilters(searchKB, setSearchKB)}

                        <div className={styles.body}>
                            {knowledgeBases.length === 0 && (
                                <div className={styles.emptyState}>
                                    <h3>Nenhuma base criada</h3>
                                    <p>Adicione conteúdos para o agente aprender e responder melhor.</p>
                                </div>
                            )}
                            {knowledgeBases.length > 0 && filteredKB.length === 0 && !isLoading && (
                                <div className={styles.emptyState}>
                                    <h3>Nenhum resultado encontrado</h3>
                                    <p>Tente ajustar sua busca.</p>
                                </div>
                            )}
                            {filteredKB.map(k => {
                                const wasUpdated = wasRecentlyUpdated(k.created_at, k.updated_at)

                                return (
                                    <div key={k._id} className={styles.agentCard}>
                                        <div>
                                            <strong>{k.name}
                                                {k.type && (
                                                    <span className={`${styles.metaInline} ${styles.badge}`}>
                                                        {getPropertyForItemInArrayById(k.type, typeOptions, "id", "value") || k.type}
                                                    </span>
                                                )}
                                            </strong>
                                            <span>{truncateText(k.description, 80)}</span>
                                            <div className={styles.meta}>
                                                <small
                                                    title={`Criado em ${formatFullDate(k.created_at)}${wasUpdated ? ` • Atualizado em ${formatFullDate(k.updated_at)}` : ''
                                                        }`}
                                                >
                                                    Criado {formatRelativeDate(k.created_at)}
                                                    {wasUpdated && ` • Atualizado ${formatRelativeDate(k.updated_at)}`}
                                                </small>
                                            </div>
                                        </div>

                                        <div className={styles.actions}>
                                            <Button variant="neutral"
                                                onClick={() => {
                                                    setKb(k)
                                                    setShowModalKB(true)
                                                }}
                                            >
                                                Editar
                                            </Button>

                                            <Button
                                                className={styles.danger}
                                                onClick={() => {
                                                    setKbToDelete(k)
                                                    setShowModalDeleteKb(true)
                                                }}
                                            >
                                                Excluir
                                            </Button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {reviewAgent && <ModalAgentReview agent={reviewAgent} closeModal={() => setReviewAgent(null)} onApply={handleApplyAgentReview} />}

            {activeTab === "tests" && (
                <div className={styles.content}>
                    <div className={styles.card}>
                        <div className={styles.header}>
                            <span className={styles.title}>Testes do agente</span>

                            {testSessions.length > 0 && (
                                <div className={styles.testHeaderActions}>
                                    <Select
                                        name="Filtrar por agente"
                                        placeholder="Todos os agentes"
                                        selected={testAgentFilterId}
                                        options={[
                                            { id: "all", value: "Todos os agentes" },
                                            ...agents.map(a => ({ id: a._id, value: a.name }))
                                        ]}
                                        onChange={setTestAgentFilterId}
                                    />

                                    <Button variant="primary" onClick={handleCreateTestSession} disabled={hasDraftWithoutMessages}>
                                        Novo teste
                                    </Button>
                                </div>
                            )}
                        </div>

                        {testSessions.length === 0 ? (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyIcon}>💬</div>

                                <h3>Nenhum teste criado ainda</h3>

                                <p>
                                    Simule conversas com o agente para validar respostas, intenções e bases de conhecimento antes de usar no atendimento real.
                                </p>

                                <div className={styles.emptyTestControls}>
                                    <Select
                                        name="Agente para o teste"
                                        placeholder="Selecione o agente"
                                        selected={testAgentId}
                                        options={agents.map(a => ({ id: a._id, value: a.name }))}
                                        onChange={handleChangeTestAgent}
                                    />

                                    <Button variant="primary" onClick={handleCreateTestSession} disabled={!testAgentId}>
                                        Novo teste
                                    </Button>
                                </div>

                                <div className={styles.chatPreview}>
                                    <div className={`${styles.msg} ${styles.user}`}>
                                        Olá, quero saber mais sobre um curso.
                                    </div>

                                    <div className={`${styles.msg} ${styles.bot}`}>
                                        Claro! Posso te ajudar com informações sobre cursos, formas de ingresso e atendimento.
                                    </div>

                                    <div className={`${styles.msg} ${styles.user}`}>
                                        Como funciona a matrícula?
                                    </div>
                                </div>

                                {!agents.length && (
                                    <div className={styles.emptyActions}>
                                        <Button variant="neutral" onClick={() => setActiveTab("agents")}>
                                            Criar agente
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className={styles.testWorkspace}>
                                <aside className={styles.testSessions}>
                                    {filteredTestSessions.length === 0 && (
                                        <div className={styles.testEmpty}>Nenhum teste para este filtro</div>
                                    )}

                                    {filteredTestSessions.map(session => (
                                        <button
                                            key={session._id}
                                            className={`${styles.testSessionItem} ${testSession?._id === session._id ? styles.activeSession : ""}`}
                                            onClick={() => handleOpenTestSession(session.agent_id?._id || session.agent_id || testAgentId, session._id)}
                                        >
                                            <strong>{session.name}</strong>
                                            <span>
                                                {isDraftTestSession(session)
                                                    ? "Rascunho"
                                                    : session.status === "closed" ? "Encerrado" : "Aberto"}
                                            </span>
                                            {getSessionAgentLabel(session) && (
                                                <small>{getSessionAgentLabel(session)}</small>
                                            )}
                                            {getSessionModelLabel(session) && (
                                                <small>{getSessionModelLabel(session)}</small>
                                            )}
                                            <small>{formatRelativeDate(session.updated_at)}</small>
                                        </button>
                                    ))}
                                </aside>

                                <section className={styles.testChat}>
                                    {hasSelectedTestSession ? (
                                        <>
                                            <div className={styles.testChatHeader}>
                                                <div className={styles.testHeaderInfo}>
                                                    {editingTestName ? (
                                                <div className={styles.testRenameForm}>
                                                    <Input
                                                        value={testNameDraft}
                                                        onChange={(e) => setTestNameDraft(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") {
                                                                e.preventDefault()
                                                                handleSaveTestSessionName()
                                                            }
                                                            if (e.key === "Escape") {
                                                                handleCancelRenameTestSession()
                                                            }
                                                        }}
                                                        maxLength={80}
                                                        autoFocus={true}
                                                    />
                                                    <Button variant="primary" onClick={handleSaveTestSessionName} disabled={isTesting}>Salvar</Button>
                                                    <Button variant="neutral" onClick={handleCancelRenameTestSession}>Cancelar</Button>
                                                </div>
                                                    ) : (
                                                <div className={styles.testTitleGroup}>
                                                    <strong>{testSession?.name || "Novo atendimento de teste"}</strong>
                                                    <span>Ambiente isolado do chat real</span>
                                                </div>
                                                    )}
                                                    {testSession && (
                                                <div className={styles.testMeta}>
                                                    {getSessionAgentLabel(testSession) && <small>{getSessionAgentLabel(testSession)}</small>}
                                                    {getSessionModelLabel(testSession) && <small>{getSessionModelLabel(testSession)}</small>}
                                                </div>
                                                    )}
                                                    {hasDraftWithoutMessages && (
                                                <div className={styles.draftAgentSelector}>
                                                    <span>Agente deste rascunho</span>
                                                    <Select
                                                        name="Agente do rascunho"
                                                        placeholder="Selecione o agente"
                                                        selected={testAgentId}
                                                        options={agents.map(a => ({ id: a._id, value: a.name }))}
                                                        onChange={handleChangeTestAgent}
                                                    />
                                                </div>
                                                    )}
                                                </div>

                                                <div className={styles.testChatActions}>
                                                    {testSession?._id && !editingTestName && (
                                                <Button variant="neutral" onClick={handleStartRenameTestSession}>
                                                    Renomear
                                                </Button>
                                                    )}

                                                    {testSession?.status !== "closed" && testSession?._id && !isDraftTestSession(testSession) && testMessages.length > 0 && (
                                                <Button variant="neutral" onClick={handleCloseTestSession}>
                                                    Encerrar
                                                </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className={styles.testChatPlaceholder}>
                                            <strong>Nenhum teste selecionado</strong>
                                            <span>Crie um novo teste ou altere o filtro para abrir um atendimento existente.</span>
                                            <Button variant="primary" onClick={handleCreateTestSession} disabled={testAgentFilterId === "all" && !testAgentId}>
                                                Novo teste
                                            </Button>
                                        </div>
                                    )}

                                    <div className={styles.testMessages}>
                                        {hasSelectedTestSession && testMessages.length === 0 && (
                                            <div className={styles.testEmpty}>Envie uma mensagem para iniciar o teste</div>
                                        )}

                                        {testMessages.map(message => (
                                            <div
                                                key={message._id}
                                                className={`${styles.testMessage} ${message.role === "assistant" ? styles.testBot : styles.testUser}`}
                                            >
                                                <span>{message.content}</span>
                                                {message.intent?.name && (
                                                    <small>Intenção detectada: {message.intent.name}</small>
                                                )}
                                                {getTestActionLabel(message.metadata?.action) && (
                                                    <small>{getTestActionLabel(message.metadata.action)}</small>
                                                )}
                                                {getTestTransferTargetLabel(message.metadata?.action, { teams, roles, users }) && (
                                                    <small>{getTestTransferTargetLabel(message.metadata.action, { teams, roles, users })}</small>
                                                )}
                                                {message.role === "assistant" && message.metadata?.automated_evaluation && (
                                                    <div className={styles.testEvaluation} data-passed={message.metadata.passed === true}>
                                                        <strong>{message.metadata.passed ? "Cenário aprovado" : "Revisar este cenário"}</strong>
                                                        {(message.metadata.issues || []).map(issue => <small key={issue}>{issue}</small>)}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className={styles.testComposer}>
                                        {isTestDebouncing && <small className={styles.testBatchStatus}>Aguardando outras mensagens antes de responder...</small>}
                                        <div className={styles.testEmojiPicker}>
                                            <Button variant="primary" className="action"
                                                onClick={() => setShowTestEmojis(prev => !prev)}
                                                disabled={!canUseTestComposer}
                                            >
                                                <MdOutlineEmojiEmotions />
                                            </Button>

                                            {showTestEmojis && canUseTestComposer && (
                                                <>
                                                    <div className={styles.testEmojiPanel}>
                                                        <Emojis onClick={addTestEmoji} />
                                                    </div>
                                                    <div className={styles.testEmojiBackdrop} onClick={() => setShowTestEmojis(false)}></div>
                                                </>
                                            )}
                                        </div>

                                        <div className={styles.testComposerInput}>
                                            <RichTextarea
                                                textareaRef={testTextareaRef}
                                                placeholder="Escreva sua mensagem..."
                                                value={testInput}
                                                onChange={setTestInput}
                                                onKeyDown={handleTestInputKeyDown}
                                                minRows={1}
                                                maxRows={5}
                                                autoFocus={true}
                                                disabled={!canUseTestComposer}
                                            />
                                        </div>

                                        <Button variant="primary" className="action"
                                            onClick={handleSendTestMessage}
                                            disabled={isTesting || !canUseTestComposer}
                                        >
                                            <MdOutlineSend />
                                        </Button>
                                    </div>
                                </section>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showModalKB && (
                <ModalKnowledgeBase
                    kb={kb}
                    typeOptions={typeOptions}
                    onSubmit={handleSubmitKB}
                    closeModal={() => {
                        setShowModalKB(false)
                        setKb({})
                    }}
                />
            )}

            {isLoading && <Loading fullPage />}
        </div>
    )
}

export default AgentBuilder
